<template>
  <div>
    <form @submit.prevent="submitForm">
      <fieldset class="mb-16">
        <legend class="form__label">When is the interview?</legend>
        <PoppinsDatePicker
          id="form-datepicker"
          v-model="startDate"
          label-name="Choose a date"
          :min-date="calendarMinDate"
          :max-date="calendarMaxDate"
          :helper-text="getStartDateHelperText()"
          :status="formStatuses.startDate"
          @blur="validateStartDate"
          @update:model-value="clearStartDateError"
          @manual-input="validate"
        />
      </fieldset>

      <fieldset class="mb-16">
        <legend class="form__label">At what time?</legend>
        <div class="flex gap-16">
          <div>
            <PoppinsTimePicker
              id="from-dropdown"
              v-model="start"
              class="w-[8.75rem]"
              label-name="From"
              :start="fromTimePickerStart"
              :end="fromTimePickerEnd"
              :helper-text="getStartTimeHelperText()"
              :status="formStatuses.start"
              @blur="validateStartTime"
              @update:model-value="clearStartTimeError"
            />
          </div>
          <div>
            <PoppinsTimePicker
              id="to-dropdown"
              v-model="end"
              class="w-[8.75rem]"
              label-name="To"
              :start="toTimePickerStart"
              :end="toTimePickerEnd"
              :helper-text="getEndTimeHelperText()"
              :status="formStatuses.end"
              @blur="validateEndTime"
              @update:model-value="clearEndTimeError"
            />
          </div>
        </div>
      </fieldset>

      <RadioButtonGroup
        :key="`radio-${virtual}`"
        v-model="virtual"
        name="Interview type"
        :options="interviewTypeOptions"
        :default-value="virtual"
        :status="formStatuses.virtual"
        class="mb-16"
        @update:model-value="clearVirtualError"
        @blur="validateVirtual"
      />
      <HelperText v-if="getVirtualHelperText()" :text="getVirtualHelperText()" :status="formStatuses.virtual" />

      <fieldset v-show="isInPerson" class="mb-16">
        <legend class="form__label">Where should {{ sitterFallback }} meet you?</legend>
        <GooglePlacesTextField
          id="google-autocomplete"
          v-model="addressInput"
          label-name="Location"
          :helper-text="getAddressHelperText()"
          :status="formStatuses.address"
          @blur="validateAddress"
          @update:model-value="clearAddressError"
          @auto-completed-addresses="handleGooglePlacesData"
        />
      </fieldset>
    </form>
  </div>
</template>

<script lang="ts">
import { startOfDay, addHours, addMinutes, formatISO, endOfDay } from 'date-fns';
import { mapGetters, mapActions } from 'vuex';

import { defaultStartDate, defaultStartTime, defaultEndTime } from '@/lib/interviews/basic_default_util';

import {
  validateRequiredFields,
  validateTimeRange,
  createFormStatuses,
  validateFieldOnBlur,
  validateConditionalField,
  validateInterviewType,
  validateAddressForInPerson,
  hasFormErrors,
  createErrorStateManager,
  createHelperTextGenerator,
  createErrorClearingManager,
  createFieldValidator,
} from '../helpers/formValidation';
import { BasicDetails } from '@/store/modules/interviewRequest/types';
import helperStatus from '@/helpers/status';
import preserveTimeOnDateChange, { getCalendarMinDate, getCalendarMaxDate, extractTimeToDate } from '../helpers/dateTimeHelpers';
import { validateStoreData, convertVirtualToString, restoreDateTimeFromStore } from '../helpers/storeIntegrationHelpers';

import GooglePlacesTextField from '@/components/addressAutoComplete/GooglePlacesTextField.vue';
import PoppinsDatePicker from '@/components/PoppinsDatePicker/PoppinsDatePicker.vue';
import PoppinsTimePicker from '@/components/timepicker/PoppinsTimePicker.vue';
import RadioButtonGroup from '@/components/radioButtonGroup/RadioButtonGroup.vue';
import HelperText from '@/components/helper_text/HelperText.vue';
import { normalizeGooglePlacesAddress } from '@/helpers/addressHelper';

const INTERVIEW_TYPE_OPTIONS = [
  {
    label: 'Phone',
    value: 'phone',
    helperText: "We'll provide {sitter}'s phone number when the interview is confirmed.",
  },
  {
    label: 'In-person',
    value: 'in_person',
    helperText: "Provide an address where you'd like to meet. (We suggest a public place, like a local coffee shop).",
  },
];

export default {
  name: 'BasicDetailsStep',
  components: {
    GooglePlacesTextField,
    PoppinsDatePicker,
    PoppinsTimePicker,
    RadioButtonGroup,
    HelperText,
  },
  data(): any {
    return {
      errors: {},
      formStatuses: createFormStatuses(['startDate', 'start', 'end', 'virtual', 'address']),
      startDate: defaultStartDate(),
      start: defaultStartTime(),
      end: defaultEndTime(),
      virtual: '',
      address: '',
      addressInput: '',
    };
  },
  computed: {
    ...mapGetters('interviewRequest', ['sitter', 'sitterFallback', 'basicDetails']),
    interviewTypeOptions() {
      const sitterName = this.sitter?.name || this.sitterFallback;
      return INTERVIEW_TYPE_OPTIONS.map((option) => ({
        ...option,
        helperText: option.helperText?.replace('{sitter}', sitterName),
      }));
    },
    errorStateManager() {
      return createErrorStateManager(this.formStatuses);
    },
    helperTextGenerator() {
      return createHelperTextGenerator(this.formStatuses);
    },
    errorClearingManager() {
      return createErrorClearingManager(this.formStatuses, this.errors);
    },
    fieldValidator() {
      return createFieldValidator(this.formStatuses, this.errors);
    },
    // we can't use helpers in the template so we wrap them in computed properties
    calendarMinDate() {
      return getCalendarMinDate();
    },
    calendarMaxDate() {
      return getCalendarMaxDate();
    },
    fromTimePickerStart(): Date {
      return startOfDay(this.startDate);
    },
    fromTimePickerEnd(): Date {
      return endOfDay(this.startDate);
    },
    toTimePickerStart(): Date {
      return addMinutes(this.start, 30);
    },
    toTimePickerEnd(): Date {
      return addHours(this.start, 15);
    },
    isPhone(): boolean {
      return this.virtual === 'phone';
    },
    isInPerson(): boolean {
      return this.virtual === 'in_person';
    },
  },
  watch: {
    startDate() {
      // Store current time values before clearing
      const currentStartTime = this.start;
      const currentEndTime = this.end;

      this.start = null;
      this.end = null;
      this.resetErrorState('startDate');

      // Preserve time values with the new date using helper function
      const { start, end } = preserveTimeOnDateChange(this.startDate, currentStartTime, currentEndTime);

      this.start = start;
      this.end = end;
    },
    start() {
      this.resetErrorState('start');
    },
    end() {
      this.resetErrorState('end');
    },
    virtual() {
      this.resetErrorState('virtual');
      // Clear address error when switching away from in-person
      if (this.isPhone) {
        this.resetErrorState('address');
      }
    },
    addressInput() {
      this.resetErrorState('address');
    },
    basicDetails: {
      handler() {
        this.loadStoredBasicDetails();
      },
      immediate: true,
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.loadStoredBasicDetails();
    });
  },
  methods: {
    ...mapActions('interviewRequest', ['saveBasicDetails']),
    // Status management helpers
    resetErrorState(fieldName: string) {
      this.errorStateManager.resetErrorState(fieldName);
    },
    triggerErrorState(fieldName: string) {
      this.errorStateManager.triggerErrorState(fieldName);
    },
    // Field validation on blur
    validateStartDate() {
      validateFieldOnBlur(this.formStatuses, 'startDate', this.startDate, ['startDate']);
    },
    validateStartTime() {
      validateFieldOnBlur(this.formStatuses, 'start', this.start, ['start']);
    },
    validateEndTime() {
      validateFieldOnBlur(this.formStatuses, 'end', this.end, ['end']);
    },
    validateVirtual() {
      validateFieldOnBlur(this.formStatuses, 'virtual', this.virtual, ['virtual']);
    },
    validateAddress() {
      validateConditionalField(this.formStatuses, 'address', this.address, ['address'], this.isInPerson);
    },
    // Error clearing on input
    clearStartDateError() {
      this.errorClearingManager.clearFieldError('startDate');
    },
    clearStartTimeError() {
      this.errorClearingManager.clearFieldError('start');
    },
    clearEndTimeError() {
      this.errorClearingManager.clearFieldError('end');
    },
    clearVirtualError() {
      this.errorClearingManager.clearFieldError('virtual');
    },
    clearAddressError() {
      this.errorClearingManager.clearFieldError('address');
    },
    // Helper text methods
    getStartDateHelperText() {
      return this.helperTextGenerator.getHelperText('startDate');
    },
    getStartTimeHelperText() {
      return this.helperTextGenerator.getHelperText('start');
    },
    getEndTimeHelperText() {
      if (this.formStatuses.end === helperStatus.Error) {
        return 'End time must be after start time';
      }
      return this.helperTextGenerator.getHelperText('end');
    },
    getVirtualHelperText() {
      return this.helperTextGenerator.getHelperText('virtual', 'Please choose either phone or in-person');
    },
    getAddressHelperText() {
      // Only show address error when interview type is in-person
      if (this.isPhone) {
        return '';
      }
      return this.helperTextGenerator.getHelperText('address', 'Please provide a meeting location');
    },
    validate(): boolean {
      this.errorClearingManager.clearFormErrors();

      // Update form statuses for all fields
      this.validateStartDate();
      this.validateStartTime();
      this.validateEndTime();
      this.validateVirtual();
      this.validateAddress();

      if (this.start && this.end && this.start >= this.end) {
        this.formStatuses.end = helperStatus.Error;
      }

      const requiredFields = ['startDate', 'start', 'end', 'virtual'];
      const fieldValues = {
        startDate: this.startDate,
        start: this.start,
        end: this.end,
        virtual: this.virtual,
      };

      const requiredValidation = validateRequiredFields(fieldValues, requiredFields);
      Object.assign(this.errors, requiredValidation.errors);

      const timeValidation = validateTimeRange(this.start, this.end);
      Object.assign(this.errors, timeValidation.errors);

      const interviewTypeValidation = validateInterviewType(this.virtual);
      Object.assign(this.errors, interviewTypeValidation.errors);

      const addressValidation = validateAddressForInPerson(this.isInPerson, this.address);
      Object.assign(this.errors, addressValidation.errors);

      return !hasFormErrors(this.formStatuses, this.errors);
    },
    submitForm(): any {
      if (this.validate()) {
        this.saveToStore();
      }
    },
    handleGooglePlacesData(placeData: any) {
      const normalizedAddress = normalizeGooglePlacesAddress(placeData);
      if (!normalizedAddress || !normalizedAddress.geoPoint) {
        return;
      }

      this.address = {
        details: normalizedAddress.formattedAddress,
        geoPoint: {
          x: normalizedAddress.geoPoint.x,
          y: normalizedAddress.geoPoint.y,
        },
      };
      this.addressInput = normalizedAddress.formattedAddress;
    },

    // Data Loading & Store Integration
    loadStoredBasicDetails(): void {
      if (!validateStoreData(this.basicDetails)) {
        return;
      }

      this.restoreVirtualField();
      this.restoreAddressFields();
      this.restoreDateTimeFields();
    },

    restoreVirtualField(): void {
      this.virtual = convertVirtualToString(this.basicDetails.virtual);
    },

    restoreAddressFields(): void {
      this.address = this.basicDetails.address || '';
      this.addressInput = this.basicDetails.address || '';
    },

    restoreDateTimeFields(): void {
      restoreDateTimeFromStore(this.basicDetails, this, extractTimeToDate);
    },

    saveToStore(): void {
      const basicDetails = this.buildBasicDetailsPayload();
      this.saveBasicDetails(basicDetails);
    },

    buildBasicDetailsPayload(): BasicDetails {
      return {
        startTime: formatISO(this.start),
        endTime: formatISO(this.end),
        virtual: this.virtual === 'phone',
        address: this.isPhone ? null : (typeof this.address === 'string' ? this.address : this.address?.details) || null,
        geoPointX: this.isPhone ? null : (typeof this.address === 'object' ? this.address?.geoPoint?.x : null) || null,
        geoPointY: this.isPhone ? null : (typeof this.address === 'object' ? this.address?.geoPoint?.y : null) || null,
      };
    },
  },
};
</script>
